import { useTranslation } from '@/i18n';
import {
  IconCollapsed,
  IconExpanded,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, ReactNode, useMemo, useState } from 'react';
import styles from './styles.module.scss';

const cx = classnames.bind(styles);

type CollapsableSearchProps = {
  initialOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  children?: (props: { isOpen: boolean }) => ReactNode;
  /**
   * NA-5172: let the wrapper follow the page width instead of the legacy
   * `min-width: 1184px` (needed by the fixed-width search rows). Set when the
   * responsive search layout is on.
   */
  fullWidth?: boolean;
};

export const CollapsableSearch: FC<CollapsableSearchProps> = ({
  initialOpen = true,
  onToggle,
  children,
  fullWidth = false,
}) => {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const { t } = useTranslation();
  const handleToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    onToggle?.(newState);
  };
  const renderTitle = useMemo(
    () =>
      isOpen
        ? t('approving_invoice_reports.search.collapse_title')
        : t('approving_invoice_reports.search.expand_title'),
    [isOpen, t]
  );

  const renderIcon = useMemo(
    () =>
      isOpen ? (
        <IconExpanded size={18} className={cx('icon')} />
      ) : (
        <IconCollapsed size={18} className={cx('icon')} />
      ),
    [isOpen]
  );

  return (
    <div
      className={cx('section-heading-wrapper', {
        [styles['section-expanded']]: isOpen,
        [styles['section-full-width']]: fullWidth,
      })}
      data-full-width={fullWidth || undefined}
    >
      {children && (
        <div
          className={cx('section-content', {
            [styles.collapsed]: !isOpen,
          })}
        >
          {children({ isOpen })}
        </div>
      )}
      <div
        className={cx('section-title-wrapper', {
          [styles['section-title-wrapper-expanded']]: isOpen,
        })}
        onClick={handleToggle}
      >
        <div className={cx('section-title')}>
          <span className={cx('title')}>{renderTitle}</span>
          <div className={cx('icon-container')}>{renderIcon}</div>
        </div>
      </div>
    </div>
  );
};
